/**
 * @file BreadCrumbs.ts
 * @description
 * -----------------------------------------------------
 * Componente personalizado para controlas las migas de pan o "breadcrumbs"
 * -----------------------------------------------------
 * @version 1.0.0
 * @created 2025-11-17
 * @author Correcta
 * @author Daniel Sentamans
 * @copyright © Correcta
 * @license Private
 */

"use client";

import { usePathname } from "next/navigation";
import { useTranslations } from "use-intl";
import { useEffect, useMemo, useState } from "react";
import { getNavigablePathsAction } from "@/actions/routing/navigable-actions";
import { isDynamicSegment, isPathNavigable } from "@/lib/routing-utils";
import { useSafeTranslation } from "@/hooks/useSafeTranslations";
import { Link } from "@/i18n/routing";
import { capitalize } from "@/lib/text";

interface ReplaceCharacter {
  from: string;
  to: string;
}

const replaceCharacterList: ReplaceCharacter[] = [
  { from: "-", to: " " },
  { from: "_", to: " " },
];

const transformLabel = (title: string): string => capitalize(title);

const LOCALES = ["es", "en", "de", "fr", "pt"] as const;

const MyBreadcrumbs = () => {
  const tBreadcrumbs = useTranslations("Breadcrumbs");
  const safeTBreadcrumbs = useSafeTranslation("Breadcrumbs");
  const ariaLabels = useTranslations("AriaLabel");
  const pathname = usePathname();
  const [navigableSet, setNavigableSet] = useState<Set<string> | null>(null);

  let segments = (pathname || "/").split("/").filter((seg) => seg.length > 0);
  // Quita el locale inicial si existe
  if (
    segments.length > 0 &&
    LOCALES.includes(segments[0] as (typeof LOCALES)[number])
  ) {
    segments = segments.slice(1);
  }

  useEffect(() => {
    void (async () => {
      const paths = await getNavigablePathsAction();
      setNavigableSet(new Set(paths));
    })();
  }, []);

  const items = useMemo(() => {
    const base = [
      {
        label: tBreadcrumbs("home"),
        href: "/campaigns",
        isNavigable: true,
      },
    ];
    const rest = segments.map((seg, idx) => {
      const href = `/${segments.slice(0, idx + 1).join("/")}`;
      const isNavigable = isPathNavigable(
        `${segments[0]}${href}`,
        navigableSet,
      );
      const isDynamic = isDynamicSegment(idx, segments, navigableSet);

      const decodedSeg = decodeURIComponent(seg);
      const staticOrTransformed = transformLabel(
        replaceCharacterList.reduce(
          (acc, r) => acc.split(r.from).join(r.to),
          decodedSeg,
        ),
      );
      let label: string;

      if (isDynamic || !navigableSet) {
        label = staticOrTransformed;
      } else {
        const translated = safeTBreadcrumbs(seg);
        label = translated ?? staticOrTransformed;
      }
      return { label, href: isNavigable ? href : null, isNavigable };
    });
    return [...base, ...rest];
  }, [tBreadcrumbs, segments, navigableSet, safeTBreadcrumbs]);

  if (!navigableSet) return null;

  return (
    <nav aria-label={ariaLabels("breadcrumb")} className="breadcrumb">
      <ol>
        {items.map((item, index) => {
          const isActive = index === items.length - 1;
          return (
            <li
              key={`bc-${index}-${item.href ?? ""}-${item.label}`}
              className="breadcrumb__item"
              aria-current={isActive ? "page" : undefined}
            >
              {isActive || !item.isNavigable || !item.href ? (
                <span>{item.label}</span>
              ) : (
                <Link href={item.href}>{item.label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default MyBreadcrumbs;
